import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { clearToken } from "../lib/auth";
import { onSessionExpired } from "../lib/apiClient";
import { useToast } from "../components/ui/ToastProvider";
import { HeroSlidesPanel } from "./admin/HeroSlidesPanel";
import { VideosPanel } from "./admin/VideosPanel";
import { NewsPanel } from "./admin/NewsPanel";
import { FilmsPanel } from "./admin/FilmsPanel";
import "./adminPro.css";

const IDLE_TIMEOUT_MINUTES = 30;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const idleTimerRef = useRef(null);

  const logout = useCallback(
    (reason) => {
      clearToken();
      if (reason) toast.notify(reason);
      navigate("/admin", { replace: true });
    },
    [navigate, toast]
  );

  // The API client fires this the moment any request comes back 401, so an
  // expired token sends the user to the login screen with an explanation
  // instead of every button failing for no visible reason.
  useEffect(() => onSessionExpired(() => logout("Your session has expired. Please sign in again.")), [logout]);

  // Client-side idle timeout. Worth stating plainly: this only clears the
  // browser's copy of the token — the server keeps honouring it until its 24h
  // expiry. Real revocation needs a server-side session or token blacklist.
  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(
        () => logout("Signed out after 30 minutes of inactivity."),
        IDLE_TIMEOUT_MINUTES * 60 * 1000
      );
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetIdleTimer));
    resetIdleTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [logout]);

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>Admin dashboard</h1>
        <button type="button" className="logout-btn" onClick={() => logout()}>
          Log out
        </button>
      </header>

      <main>
        <HeroSlidesPanel />
        <VideosPanel />
        <NewsPanel />
        <FilmsPanel />
      </main>
    </div>
  );
}
